/**
 * ListaCompacta — peças do redesenho mobile do módulo legado (2026-09-24).
 *
 * Abaixo de 1024px (celular e tablet) toda lista tabular vira o card
 * compacto de 3 linhas escolhido pelo dono (opção B do mockup), alinhado
 * à esquerda:
 *
 *   ┌──────────────────────────────────┐
 *   │ TÍTULO                 ● Vencido │  CardTopo: CardTitulo + .selo
 *   │ linha secundária                 │  CardSub
 *   │ entrega 10/11/26 · fila 236 · …  │  CardMeta: .par > .sep .k b
 *   └──────────────────────────────────┘
 *
 * Também exporta os pedaços de CSS reutilizáveis pelas telas com layout
 * próprio (KPIs em faixa rolável de tiles, botão só-ícone), para que
 * Acompanhamento, Marcenaria, Terceiros etc. fiquem iguais ao PainelTabela
 * sem copiar CSS.
 *
 * Referência de medidas: card 76px, primeiro item a ~220px do topo, 7 a 8
 * itens por tela em 375px; duas colunas entre 769 e 1024px.
 */
import styled, { css } from "styled-components";
import { BP_MOBILE, mq } from "../../styles/canonical/breakpoints";

// Tablet: 769–1024px.
export const mqTabletApenas = `(min-width: ${BP_MOBILE + 1}px) and ${mq.tablet}`;

export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* Tablet: dois cards por linha — um card de 76px esticado em 900px de
     largura desperdiça metade da tela. */
  @media ${mqTabletApenas} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const CardItem = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 9px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  color: ${({ theme }) => theme.textPrimary};
  ${({ $clicavel }) => ($clicavel ? "cursor: pointer;" : "")}
  /* Barra de estado à esquerda, a mesma assinatura da 1ª célula da tabela.
     $barra = nome do tom (danger/warning/success/info). */
  ${({ $barra }) =>
    $barra && $barra !== "default"
      ? `box-shadow: inset 3px 0 0 var(--${$barra}-text);`
      : ""}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

export const CardTopo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;

  > .selo {
    flex: 0 0 auto;
    display: inline-flex;
  }
`;

export const CardTitulo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardSub = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Linha de metadados: "rótulo valor · rótulo valor". O separador vive
   DENTRO do par (.par > .sep) para nunca ficar pendurado no fim da linha
   quando o texto quebra. */
export const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 6px;
  row-gap: 0;
  font-size: var(--text-min, 12px);
  line-height: 1.35;
  color: ${({ theme }) => theme.textSecondary};
  font-variant-numeric: tabular-nums;

  .par {
    white-space: nowrap;
  }
  .k {
    color: ${({ theme }) => theme.textTertiary};
  }
  b {
    font-weight: 700;
    color: ${({ theme }) => theme.textPrimary};
  }
  .sep {
    color: ${({ theme }) => theme.textTertiary};
    opacity: 0.7;
  }
`;

/* Ação única do card (telas em que o operador age item a item). */
export const CardAcoes = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding-top: 4px;

  > .principal {
    flex: 1 1 auto;
    justify-content: center;
    min-height: var(--tap-min, 44px);
  }
`;

export const CardVazio = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 9px;
`;

/* Grupo dentro da lista ("Vencidos · 19"). */
export const CardGrupo = styled.div`
  font-size: var(--text-min, 12px);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary};
  padding: 8px 2px 2px;

  @media ${mqTabletApenas} {
    grid-column: 1 / -1;
  }
`;

// ---------------------------------------------------------------------------
// Pedaços de CSS para as telas com layout próprio
// ---------------------------------------------------------------------------

/* Container de KPIs: no celular/tablet vira faixa rolável de tiles. Aplicar
   no styled do grid de resumo da tela (`${kpiFaixaCss}`). O margin negativo
   deixa a faixa sangrar até a borda da tela (padding 16px do PageContainer). */
export const kpiFaixaCss = css`
  @media ${mq.tablet} {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    margin: 0 -16px;
    padding: 0 16px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

/* Tile de KPI: número em cima (16px), rótulo embaixo (12px), sem sublinha.
   Aplicar no styled do card de resumo da tela. As classes .kpi-num / .kpi-label
   / .kpi-sub são opcionais — use quando o card tiver filhos sem styled próprio. */
export const kpiTileCss = css`
  @media ${mq.tablet} {
    flex: 0 0 auto;
    min-width: 92px;
    padding: 7px 10px;
    border-radius: 10px;
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 3px;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;

    .kpi-num {
      font-size: 16px;
      font-weight: 700;
      line-height: 1;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
    .kpi-label {
      font-size: var(--text-min, 12px);
      letter-spacing: 0.04em;
      margin: 0;
      white-space: nowrap;
    }
    .kpi-sub {
      display: none;
    }
  }
`;

/* Botão que vira só ícone (36px) no celular/tablet: o texto some, mas o
   aria-label/title continua. Envolver o texto em <span className="rotulo">. */
export const botaoSoIconeCss = css`
  @media ${mq.tablet} {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    min-height: 36px;
    padding: 0;
    gap: 0;
    justify-content: center;
    border-radius: 9px;

    .rotulo {
      display: none;
    }
  }
`;

/* Wrapper para um botão de terceiros (ex.: BotaoExportacao) virar só ícone. */
export const SoIcone = styled.span`
  display: contents;

  @media ${mq.tablet} {
    > button {
      width: 36px;
      min-height: 36px;
      padding: 0;
      gap: 0;
      font-size: 0;
      border-radius: 9px;
      justify-content: center;
    }
    > button svg {
      width: 15px;
      height: 15px;
    }
  }
`;

/* Escondido no celular/tablet. */
export const SoDesktop = styled.span`
  display: contents;

  @media ${mq.tablet} {
    display: none;
  }
`;

/* Só no celular/tablet. */
export const SoCompacto = styled.span`
  display: none;

  @media ${mq.tablet} {
    display: contents;
  }
`;
